import { ERROR_CODES, PRINTER_TYPES, isValidIPAddress, isValidCmd } from "./Utils";
import PrinterError from "./PrinterError";

const net = require("net"); // Node.js built-in module for networking
const { ipcRenderer } = require("electron");


// Function to send the print command to the printer via TCP/IP
const sendToNetworkPrinter = async (printerIP, command) => {
    const printerPort = 9100;

    // Validate input parameters
    if (!printerIP || !isValidIPAddress(printerIP)) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Invalid printer IP address.");
    }
    if (!isValidCmd(command)) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Command must be a Uint8Array.");
    }

    return new Promise((resolve, reject) => {
        const client = net.createConnection(printerPort, printerIP);

        // Handle successful connection
        client.on("connect", () => {
            try {
                client.write(command); // Write the Uint8Array directly to the stream
                client.end(); // Gracefully close the connection
            } catch (error) {
                reject(new PrinterError(ERROR_CODES.UNKNOWN, `Failed to send command: ${error.message}`));
            }
        });

        // Handle connection errors
        client.on("error", (error) => {
            client.destroy();
            reject(new PrinterError(ERROR_CODES.CONNECTION_ERROR, `Connection error: ${error.message}`));
        });

        // Handle connection closure
        client.on("close", () => {
            resolve(true);
        });

        // Handle timeout
        client.setTimeout(5000); // Configurable timeout (5 seconds)
        client.on("timeout", () => {
            client.destroy();
            reject(new PrinterError(ERROR_CODES.TIMEOUT, "Printer connection timed out."));
        });
    });
};

// Get the list of USB devices
export const getUsbDevices = async () => {
    if (!navigator.usb) {
        throw new PrinterError(ERROR_CODES.UNKNOWN, "WebUSB is not supported in this environment.");
    }

    try {
        const devices = await navigator.usb.getDevices();
        return devices.map((device) => ({
            id: device.productId,
            name: device.productName,
        }));
    } catch (error) {
        throw new PrinterError(ERROR_CODES.UNKNOWN, `Failed to fetch USB devices: ${error.message}`);
    }
};

// Send the print command to the USB printer
export const sendToUsbPrinter = async (printerId, command) => {
    if (!printerId) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Printer ID is required.");
    }
    if (!isValidCmd(command)) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Command must be a Uint8Array.");
    }

    let printer = null;

    try {
        const devices = await navigator.usb.getDevices();
        printer = devices.find((device) => device.productId === printerId);

        if (!printer) {
            throw new PrinterError(ERROR_CODES.UNKNOWN, "Printer not found.");
        }

        await printer.open();

        if (!printer.configuration) {
            await printer.selectConfiguration(1);
        }
        await printer.claimInterface(0);

        const endpoint = printer.configuration.interfaces[0].alternate.endpoints.find(
            (ep) => ep.direction === "out"
        );

        if (!endpoint) {
            throw new PrinterError(ERROR_CODES.UNKNOWN, "Output endpoint not found.");
        }

        const result = await printer.transferOut(endpoint.endpointNumber, command);

        if (result.status !== "ok") {
            throw new PrinterError(ERROR_CODES.UNKNOWN, `Failed to send command: ${result.status}`);
        }

        return true;
    } catch (error) {
        if (error instanceof PrinterError) {
            throw error; // Re-throw known errors
        }
        throw new PrinterError(ERROR_CODES.UNKNOWN, `Unexpected error: ${error.message}`);
    } finally {
        try {
            if (printer) {
                await printer.close();
            }
        } catch (closeError) {
            console.error("Failed to close the printer connection:", closeError.message);
        }
    }
};

// Get the list of Bluetooth devices
export const getBluetoothDevices = async () => {
    try {
        await navigator.bluetooth.requestDevice({ acceptAllDevices: true });

        ipcRenderer.send("get-bluetooth-devices"); // Ask main process for saved printers

        return new Promise((resolve, reject) => {
            ipcRenderer.once("receive-bluetooth-devices", (event, bluetoothDevices) => {
                // console.log("Printers received from main:", bluetoothDevices);
                resolve(bluetoothDevices);
            });

            setTimeout(() => {
                const err = new PrinterError(ERROR_CODES.UNKNOWN, "Timeout waiting for Bluetooth devices");
                console.error(err)
                reject(err);
            }, 10000);
        });
    } catch (error) {
        console.error("Error getting Bluetooth device:", error);
        throw new PrinterError(ERROR_CODES.UNKNOWN, `Failed to fetch bluetooth devices: ${error.message}`);
    }
};

// Function to send the print command to the printer via Bluetooth
// @Todo: need to be corrected
const sendToBluetoothPrinter = async (deviceId, command) => {
    // console.log("invoking bluetooth printer....")
    if (!deviceId) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Invalid printer ID.");
    }
    if (!isValidCmd(command)) {
        throw new PrinterError(ERROR_CODES.INVALID_INPUT, "Command must be a Uint8Array.");
    }

    return new Promise(async (resolve, reject) => {
        try {
            // console.log("trigger")
            // Request device with Bluetooth printing service (you can replace with specific service UUID)
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
            });

            // console.log('Connected to device:', device);

            // Connect to the device's GATT server
            const server = await device.gatt.connect();

            // console.log({server})

            // Get the 'printer service' (replace with actual service UUID)
            const service = await server.getPrimaryService('00001802-0000-1000-8000-00805f9b34fb'); // Example, change to actual service UUID

            // Get the characteristic that supports writing print commands (e.g., 'printer_data')
            const characteristic = await service.getCharacteristic('printer_data'); // Replace with actual characteristic UUID

            // Write the data to the printer
            await characteristic.writeValue(command);

            // console.log('Print command sent successfully');
            resolve(true)
        } catch (error) {
            reject(PrinterError(ERROR_CODES.UNKNOWN, `Error during bluetooth operation: ${error.message}`));
        }
    });
}

// Printer management module
const electronPrinter = {
    sendToNetworkPrinter,
    sendToUsbPrinter,
    sendToBluetoothPrinter,
    getUsbDevices,
    getBluetoothDevices,
    getSupportedPrinterTypes() {
        return [
            PRINTER_TYPES.NO_PRINT,
            PRINTER_TYPES.NETWORK,
            // PRINTER_TYPES.BLUETOOTH,
            // PRINTER_TYPES.USB,
        ]
    }
};

export default electronPrinter;
